import actions from './actions';

const initState = {
  isModalOpen: false,
  isMinimized: false,
  isRecording: false,
  isPaused: false,
  recordingTime: 0,
  audioLevel: 0,
  stream: null,
  mediaRecorder: null,
  audioData: null,
  audioChunks: [],
  availableMicrophones: [],
  selectedMicrophoneId: '',
  widgetPosition: 'bottom-left',
  isProcessing: false,
  transcription: '',
  report: '',
  recordingStartTime: null,
  totalElapsedTime: 0,
  pauseStartTime: null,
  error: null,
  errorType: null,
  loading: false,
  currentRecordingId: null,
  selectedSeectedId: null,
  editedTranscription: '',
  isTranscriptionEdited: false,
  recordingMimeType: 'audio/webm',
  editedReport: '',
  isReportEdited: false,
  isHelpModalOpen: false,
  isTestModalOpen: false,
  isTimeWarning: false,
  timeRemaining: null,
  maxRecordingTime: 5400,
  isTimeLimitReached: false,
  processingProgress: 0,
  recordFieldMode: null,
  permissions: null,
  pendingAudioBlob: null,
  isRetranscribing: false,
  hasRetranscribed: false,
};

export default function medicalRecorderReducer(state = initState, action) {
  switch (action.type) {
    case actions.AI_RECORDER_OPEN_MODAL:
      return {
        ...state,
        isModalOpen: true,
        isMinimized: false,
        error: null,
        errorType: null,
        transcription: state.audioData ? state.transcription : '',
        report: state.audioData ? state.report : '',
      };
    case actions.AI_RECORDER_CLOSE_MODAL:
      return {
        ...state,
        isModalOpen: false,
        isMinimized: false,
      };
    case actions.AI_RECORDER_MINIMIZE_MODAL:
      return {
        ...state,
        isMinimized: true,
      };
    case actions.AI_RECORDER_EXPAND_MODAL:
      return {
        ...state,
        isMinimized: false,
      };

    case actions.AI_RECORDER_START_RECORDING:
      return {
        ...state,
        loading: true,
        error: null,
        errorType: null,
      };
    case actions.AI_RECORDER_START_RECORDING_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
      };
    case actions.AI_RECORDER_START_RECORDING_SUCCESS:
      return {
        ...state,
        isRecording: true,
        isPaused: false,
        loading: false,
        stream: action.payload.stream,
        mediaRecorder: action.payload.mediaRecorder,
        currentRecordingId: action.payload.recordingId,
        audioChunks: [],
        recordingStartTime: Date.now(),
        totalElapsedTime: 0,
        recordingMimeType: action.payload.mimeType || 'audio/webm',
      };

    case actions.AI_RECORDER_PAUSE_RECORDING_SUCCESS:
      return {
        ...state,
        isPaused: true,
        pauseStartTime: Date.now(),
      };
    case actions.AI_RECORDER_RESUME_RECORDING_SUCCESS:
      return {
        ...state,
        isPaused: false,
        recordingStartTime: Date.now(),
        pauseStartTime: null,
      };
    case actions.AI_RECORDER_FINALIZE_RECORDING:
      return {
        ...state,
        loading: true,
      };
    case actions.AI_RECORDER_FINALIZE_RECORDING_SUCCESS:
      return {
        ...state,
        // Recording state cleanup
        isRecording: false,
        isPaused: false,
        isMinimized: false,
        recordingTime: 0,
        audioLevel: 0,
        stream: null,
        mediaRecorder: null,
        audioChunks: [],
        recordingStartTime: null,
        totalElapsedTime: 0,
        pauseStartTime: null,
        currentRecordingId: null,
        recordingMimeType: 'audio/webm',
        loading: false,
        isTimeWarning: false,
        timeRemaining: null,
        isTimeLimitReached: false,
        // New audio data for processing
        audioData: action.payload.audioData,
        isProcessing: true,
      };

    case actions.AI_RECORDER_FETCH_MICROPHONES_SUCCESS:
      return {
        ...state,
        availableMicrophones: action.payload.microphones,
        selectedMicrophoneId: state.selectedMicrophoneId || (action.payload.microphones.length > 0 ? action.payload.microphones[0].deviceId : ''),
      };
    case actions.AI_RECORDER_CHANGE_MICROPHONE_SUCCESS:
      return {
        ...state,
        selectedMicrophoneId: action.payload.microphoneId,
        stream: action.payload.stream,
      };
    case actions.AI_RECORDER_CHANGE_MICROPHONE_FAILURE:
      return {
        ...state,
        error: action.payload.error,
      };
    case actions.AI_RECORDER_CHANGE_WIDGET_POSITION_SUCCESS:
      return {
        ...state,
        widgetPosition: action.payload.position,
      };

    case actions.AI_RECORDER_PROCESS_AUDIO:
      return {
        ...state,
        isProcessing: true,
        error: null,
        errorType: null,
        pendingAudioBlob: action.payload?.audioBlob || state.pendingAudioBlob,
      };
    case actions.AI_RECORDER_PROCESS_AUDIO_SUCCESS:
      return {
        ...state,
        isProcessing: false,
        isRetranscribing: false,
        transcription: action.payload.transcription,
        report: action.payload.report,
        transcriptionFiles: action.payload.transcriptionFiles,
        pendingAudioBlob: null,
      };
    case actions.AI_RECORDER_PROCESS_AUDIO_FAILURE:
      return {
        ...state,
        isProcessing: false,
        isRetranscribing: false,
        error: action.payload.error,
        errorType: action.payload.errorType || null,
      };
    case actions.AI_RECORDER_RETRANSCRIBE_AUDIO:
      return {
        ...state,
        isProcessing: true,
        isRetranscribing: true,
        error: null,
        errorType: null,
        processingProgress: 0,
        // The alternative AI produces a fresh transcription/report, so any
        // manual edits to the previous result no longer apply.
        editedTranscription: '',
        isTranscriptionEdited: false,
        editedReport: '',
        isReportEdited: false,
        // Only one alternative transcription is allowed per recording.
        hasRetranscribed: true,
      };
    case actions.AI_RECORDER_RETRY_PROCESS_AUDIO:
      return {
        ...state,
        isProcessing: true,
        error: null,
        errorType: null,
        processingProgress: 0,
      };

    case actions.AI_RECORDER_UPDATE_RECORDING_TIME:
      return {
        ...state,
        recordingTime: action.payload.time,
      };
    case actions.AI_RECORDER_UPDATE_AUDIO_LEVEL:
      return {
        ...state,
        audioLevel: action.payload.level,
      };
    case actions.AI_RECORDER_SET_TIME_WARNING:
      return {
        ...state,
        isTimeWarning: action.payload.isWarning,
        timeRemaining: action.payload.timeRemaining,
      };
    case actions.AI_RECORDER_TIME_LIMIT_REACHED:
      return {
        ...state,
        isTimeLimitReached: true,
      };

    case actions.AI_RECORDER_SET_ERROR:
      return {
        ...state,
        error: action.payload.error,
      };
    case actions.AI_RECORDER_CLEAR_ERROR:
      return {
        ...state,
        error: null,
        errorType: null,
      };

    case actions.AI_RECORDER_RESET_RECORDER:
      return {
        ...initState,
        permissions: state.permissions,
        widgetPosition: state.widgetPosition,
      };

    case actions.AI_RECORDER_ADD_AUDIO_CHUNK:
      return {
        ...state,
        audioChunks: [...state.audioChunks, action.payload.chunkIndex],
      };

    case actions.AI_RECORDER_SET_SELECTED_ID:
      return {
        ...state,
        selectedSeectedId: action.payload.selectedId,
        recordFieldMode: action.payload.recordFieldMode,
      };

    case actions.AI_RECORDER_UPDATE_TRANSCRIPTION:
      return {
        ...state,
        editedTranscription: action.payload.transcription,
        isTranscriptionEdited: action.payload.transcription !== state.transcription,
      };

    case actions.AI_RECORDER_REGENERATE_REPORT:
      return {
        ...state,
        isProcessing: true,
        error: null,
      };

    case actions.AI_RECORDER_UPDATE_REPORT:
      return {
        ...state,
        editedReport: action.payload.report,
        isReportEdited: action.payload.report !== state.report,
      };

    case actions.AI_RECORDER_STOP_RECORDING_SUCCESS:
    case actions.AI_RECORDER_DELETE_RECORDING_SUCCESS:
    case actions.AI_RECORDER_CLEAR_AUDIO_CHUNKS:
      return {
        ...state,
        // Complete recording state cleanup
        isRecording: false,
        isPaused: false,
        isMinimized: false,
        recordingTime: 0,
        audioLevel: 0,
        stream: null,
        mediaRecorder: null,
        audioChunks: [],
        recordingStartTime: null,
        totalElapsedTime: 0,
        pauseStartTime: null,
        currentRecordingId: null,
        selectedSeectedId: null,
        recordingMimeType: 'audio/webm',
        loading: false,
        isTimeWarning: false,
        timeRemaining: null,
        isTimeLimitReached: false,
        // Clear any audio data since recording was stopped/deleted
        audioData: null,
        isProcessing: false,
        isRetranscribing: false,
        recordFieldMode: null,
        // Clear transcription/report data for delete case
        ...(action.type === actions.AI_RECORDER_DELETE_RECORDING_SUCCESS && {
          transcription: '',
          report: '',
          editedTranscription: '',
          editedReport: '',
          isTranscriptionEdited: false,
          isReportEdited: false,
        }),
      };

    case actions.AI_RECORDER_SHOW_HELP_MODAL:
      return {
        ...state,
        isHelpModalOpen: true,
      };

    case actions.AI_RECORDER_SHOW_HELP_MODAL_CLOSE:
      return {
        ...state,
        isHelpModalOpen: false,
      };

    case actions.AI_RECORDER_SET_PROCESSING_STEP:
      return {
        ...state,
        processingProgress: action.payload.progress,
      };

    case actions.AI_RECORDER_OPEN_TEST_MODAL:
      return {
        ...state,
        isTestModalOpen: true,
      };

    case actions.AI_RECORDER_CLOSE_TEST_MODAL:
      return {
        ...state,
        isTestModalOpen: false,
      };

    case actions.AI_RECORDER_GET_PERMISSION_SUCCESS:
      return {
        ...state,
        ...action.payload,
      };

    default:
      return state;
  }
}
